/**
 * Staff-auth token store + authed fetch wrapper for the internal admin console.
 *
 * The staff JWT is persisted in sessionStorage (cleared when the tab closes —
 * an internal operator tool, not a long-lived consumer session). Every admin
 * API call goes through `authedFetch`, which attaches `Authorization: Bearer`
 * and treats ANY 401 as "the session is gone": it clears the token and bounces
 * the operator back to /admin/login. Components never have to special-case 401.
 */

import { ApiError } from './api';

const TOKEN_KEY = 'cp_staff_token';
const ROLE_KEY = 'cp_staff_role';
const LOGIN_PATH = '/admin/login';

/** Raised when a request comes back 401 — the caller's view is already being
 *  redirected, so it should stop rendering rather than show an error panel. */
export class UnauthorizedError extends ApiError {
  constructor() {
    super('Your session has expired. Please sign in again.', 401);
    this.name = 'UnauthorizedError';
  }
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getRole(): string | null {
  try {
    return sessionStorage.getItem(ROLE_KEY);
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

export function setSession(token: string, role: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(ROLE_KEY, role);
  } catch {
    /* storage unavailable (private mode) — the token simply won't persist */
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ROLE_KEY);
  } catch {
    /* ignore */
  }
}

/** Hard-redirect to the login screen. Used on 401 so a stale token can't get
 *  stuck mid-app. A full navigation also resets any in-memory view state. */
function redirectToLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

/**
 * Fetch wrapper for staff-gated admin endpoints.
 *
 * - Attaches `Authorization: Bearer <token>` (and JSON content-type for bodies).
 * - On 401: clears the session, redirects to /admin/login, and throws
 *   `UnauthorizedError` so the calling view stops rather than rendering an error.
 * - On other non-2xx: throws `ApiError` with a parsed `detail` (FastAPI shape)
 *   and a human message, so views can surface backend guard failures verbatim.
 * - Network failures throw `ApiError(…, 0)`.
 */
export async function authedFetch<T>(
  input: string,
  init: RequestInit = {},
  fallbackMessage = 'Something went wrong. Please try again.',
): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let resp: Response;
  try {
    resp = await fetch(input, { ...init, headers });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }

  if (resp.status === 401) {
    clearSession();
    redirectToLogin();
    throw new UnauthorizedError();
  }

  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = await resp.json();
    } catch {
      detail = undefined;
    }
    throw new ApiError(detailMessage(detail) ?? fallbackMessage, resp.status, detail);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/**
 * Pull a human-readable message out of a FastAPI error body.
 * FastAPI returns `{detail: "..."}` for HTTPException and `{detail: [...]}`
 * for 422 validation errors — surface the former verbatim (it carries the
 * backend guard reasoning, e.g. the go-live agreement gate).
 */
export function detailMessage(detail: unknown): string | null {
  if (detail && typeof detail === 'object' && 'detail' in detail) {
    const d = (detail as { detail: unknown }).detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length > 0) {
      const first = d[0] as { msg?: string; loc?: unknown[] };
      if (first?.msg) {
        const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : '';
        return field ? `${String(field)}: ${first.msg}` : first.msg;
      }
    }
  }
  return null;
}
